import { z } from "zod";
import { ProjectChallenges } from "../../../__generated__/graphql";

// Step 1: User Info Schema with conditional validation
export const userInfoSchema = z.object({
  userType: z.enum(['homeowner', 'business'] as const, {
    errorMap: () => ({ message: 'Please select user type' }),
  }),
  // Homeowner fields
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  // Business fields  
  organization: z.string().optional(),
  roleInOrganization: z.string().optional(),
  industry: z.string().optional(),
}).refine((data) => {
  // If homeowner, firstName and lastName are required
  if (data.userType === 'homeowner') {
    return data.firstName && data.firstName.trim().length > 0 && 
           data.lastName && data.lastName.trim().length > 0;
  }
  // If business, organization and roleInOrganization are required
  if (data.userType === 'business') {
    return data.organization && data.organization.trim().length > 0 && 
           data.roleInOrganization && data.roleInOrganization.trim().length > 0;
  }
  return true;
}, {
  message: "Please fill in all required fields for your user type",
  path: ["userType"], // Show error on userType field
});

// Step 2: Contact Info Schema
export const contactInfoSchema = z.object({
  email: z.string().email("Please enter a valid email address"),
  phone: z.string().min(10, "Please enter a valid phone number"),
});

// Step 3: Project Details Schema
export const projectDetailsSchema = z.object({
  projectType: z.enum(['one-off', 'short-term-contract', 'long-term-ongoing'] as const, {
    errorMap: () => ({ message: 'Please select a project type' }),
  }),
  managementType: z.enum(['laborhack-managed', 'shared-management', 'self-managed'] as const, {
    errorMap: () => ({ message: 'Please select a management type' }),
  }),
  description: z.string().min(50, "Please provide a detailed description (minimum 50 characters)"),
  keySkillsRequired: z.string().min(10, "Please specify the key skills required (minimum 10 characters)"),
  expectedTeamSize: z.number()
    .min(1, "Team size must be at least 1")
    .max(100, "Team size cannot exceed 100"),
});

// Step 4: Project Preferences Schema
export const projectPreferencesSchema = z.object({
  challengesToAvoid: z.array(z.nativeEnum(ProjectChallenges)).optional(),
  otherProjectChallenges: z.string().optional(),
}).refine((data) => {
  // Must have either predefined challenges OR custom text, not neither
  const hasChallenges = data.challengesToAvoid && data.challengesToAvoid.length > 0;
  const hasOtherText = data.otherProjectChallenges && data.otherProjectChallenges.trim().length > 0;
  
  return hasChallenges || hasOtherText;
}, {
  message: "Please either select challenges to avoid or specify other challenges",
  path: ["challengesToAvoid"],
});

// Combined schema for final validation
export const projectOutsourcingSchema = z.object({
  // User info fields
  userType: z.enum(['homeowner', 'business'] as const),
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  organization: z.string().optional(),
  roleInOrganization: z.string().optional(),
  industry: z.string().optional(),
  // Contact info fields
  email: z.string().email("Please enter a valid email address"),
  phone: z.string().min(10, "Please enter a valid phone number"),
  // Project details fields
  projectType: z.enum(['one-off', 'short-term-contract', 'long-term-ongoing'] as const),
  managementType: z.enum(['laborhack-managed', 'shared-management', 'self-managed'] as const),
  description: z.string().min(50, "Please provide a detailed description (minimum 50 characters)"),
  keySkillsRequired: z.string().min(10, "Please specify the key skills required (minimum 10 characters)"),
  expectedTeamSize: z.number().min(1, "Team size must be at least 1").max(100, "Team size cannot exceed 100"),
  // Project preferences fields
  challengesToAvoid: z.array(z.nativeEnum(ProjectChallenges)).optional(),
  otherProjectChallenges: z.string().optional(),
}).refine((data) => {
  // If homeowner, firstName and lastName are required
  if (data.userType === 'homeowner') {
    return data.firstName && data.firstName.trim().length > 0 && 
           data.lastName && data.lastName.trim().length > 0;
  }
  // If business, organization and roleInOrganization are required
  if (data.userType === 'business') {
    return data.organization && data.organization.trim().length > 0 && 
           data.roleInOrganization && data.roleInOrganization.trim().length > 0;
  }
  return true;
}, {
  message: "Please fill in all required fields for your user type",
  path: ["userType"],
}).refine((data) => {
  // Must have either predefined challenges OR custom text, not neither
  const hasChallenges = data.challengesToAvoid && data.challengesToAvoid.length > 0;
  const hasOtherText = data.otherProjectChallenges && data.otherProjectChallenges.trim().length > 0;
  
  return hasChallenges || hasOtherText;
}, {
  message: "Please either select challenges to avoid or specify other challenges",
  path: ["challengesToAvoid"],
});

// Type definitions
export type UserInfoFormData = z.infer<typeof userInfoSchema>;
export type ContactInfoFormData = z.infer<typeof contactInfoSchema>;
export type ProjectDetailsFormData = z.infer<typeof projectDetailsSchema>;
export type ProjectPreferencesFormData = z.infer<typeof projectPreferencesSchema>;
export type ProjectOutsourcingFormData = z.infer<typeof projectOutsourcingSchema>;